package org.teddapp.project.core

/**
 * Fabrique les faits écrits dans le journal du noyau. Un seul endroit sait comment
 * un geste de l'interface se traduit en événement, et l'échappement JSON n'est écrit
 * qu'une fois.
 *
 * Les noms de champs suivent le registre du noyau, en français : c'est lui qui fait foi.
 */
object CoreFacts {

    /** Compétence de repli quand la course n'en porte aucune : le temps compte quand même. */
    const val UNCLASSIFIED = "non-classe"

    fun focusStarted(competenceId: String, taskName: String): String =
        obj("FocusStarted") {
            put("competence_id", competenceId)
            put("tache_nom", taskName)
        }

    fun focusCompleted(competenceId: String, taskName: String, seconds: Long, explanation: String, feuilleId: String? = null): String =
        obj("FocusCompleted") {
            put("competence_id", competenceId)
            put("tache_nom", taskName)
            putRaw("duree_secondes", seconds.toString())
            put("auto_explication", explanation)
            if (feuilleId != null) put("feuille_id", feuilleId)
        }

    /** Une étape de parcours telle que le noyau la reçoit. */
    data class ParcoursStepFact(
        val code: String,
        val title: String,
        val isProject: Boolean,
        val level: String?,
        val leaves: List<ParcoursLeafFact>,
    )

    /** Une feuille de parcours : micro d'un module ou action d'un projet. */
    data class ParcoursLeafFact(val id: String, val title: String, val minutes: Int, val kind: String?)

    /**
     * Déclaration d'un parcours : la suite ordonnée des étapes et leurs feuilles. Le noyau
     * en tire l'étape courante, la prochaine feuille et le palier atteint ; une nouvelle
     * déclaration remplace la précédente sans perdre ce qui est fait.
     */
    fun parcoursDeclared(competenceId: String, title: String, steps: List<ParcoursStepFact>): String =
        obj("ParcoursDeclared") {
            put("competence_id", competenceId)
            put("titre", title)
            val etapes = steps.joinToString(",") { s ->
                val feuilles = s.leaves.joinToString(",") { f ->
                    "{" + escape("id") + ":" + escape(f.id) + "," +
                        escape("titre") + ":" + escape(f.title) + "," +
                        escape("minutes") + ":" + f.minutes.coerceAtLeast(1) + "," +
                        escape("nature") + ":" + escape(f.kind.orEmpty()) + "}"
                }
                "{" + escape("code") + ":" + escape(s.code) + "," +
                    escape("titre") + ":" + escape(s.title) + "," +
                    escape("genre") + ":" + escape(if (s.isProject) "projet" else "module") + "," +
                    escape("palier") + ":" + (s.level?.let { escape(it) } ?: "null") + "," +
                    escape("feuilles") + ":[" + feuilles + "]}"
            }
            putRaw("etapes", "[$etapes]")
        }

    fun parcoursLeafCompleted(competenceId: String, leafId: String): String =
        obj("ParcoursLeafCompleted") {
            put("competence_id", competenceId)
            put("feuille_id", leafId)
        }

    fun parcoursLeafReopened(competenceId: String, leafId: String): String =
        obj("ParcoursLeafReopened") {
            put("competence_id", competenceId)
            put("feuille_id", leafId)
        }

    fun cravingObserved(habit: String, sensations: String, yielded: Boolean, intensity: Int): String =
        obj("CravingObserved") {
            put("habitude_id", habit)
            put("sensations_physiques", sensations)
            putRaw("pulsion_cedee", yielded.toString())
            putRaw("intensite", intensity.coerceIn(0, 10).toString())
        }

    /**
     * Dépense enregistrée, avec le verdict du filtre cartésien. Le noyau en tire le taux
     * de décisions rationnelles et la cohérence entre le temps de pratique et l'argent.
     *
     * [rational] vaut vrai pour un besoin réel comme pour un désir maîtrisé : dans les
     * deux cas la décision a été prise en conscience.
     */
    fun expenseLogged(
        id: String,
        amount: Double,
        currency: String,
        category: String,
        rational: Boolean,
        justification: String,
    ): String = obj("ExpenseLogged") {
        put("id", id)
        putRaw("montant", formatAmount(amount))
        put("devise", currency)
        put("categorie", category)
        put("decision", if (rational) "rationnelle" else "impulsive")
        put("validation_cartesienne", justification)
    }

    /**
     * Habitude tenue aujourd'hui. Distinct de l'envie observée : ici rien n'a été
     * ressenti à traverser, la journée s'est simplement passée comme voulu.
     */
    fun habitValidated(habit: String): String =
        obj("HabitValidated") {
            put("habitude_id", habit)
        }

    /**
     * Pause active terminée (Risser) : une minute d'effort qui stimule le BDNF.
     * Sans capteur, le pouls vaut zéro et le score se déduit de la durée tenue.
     */
    fun bdnfActivity(exercise: String, seconds: Int, heartRate: Int, efficiency: Double): String =
        obj("BdnfActivityCompleted") {
            put("exercice", exercise)
            // Le noyau refuse une durée nulle et un score hors de [0, 1] : on borne ici.
            putRaw("duree_secondes", seconds.coerceAtLeast(1).toString())
            putRaw("frequence_cardiaque_moyenne", heartRate.coerceAtLeast(0).toString())
            putRaw("score_efficacite", formatAmount(efficiency.coerceIn(0.0, 1.0)))
        }

    /**
     * Tentative dans une langue : ce qui a été produit, tel quel, avec l'intention visée.
     * Le noyau la reformule et propose la voix du parent qui reprend sans corriger sèchement.
     */
    fun languageAttempt(language: String, attempt: String, intention: String): String =
        obj("LanguageAttempt") {
            put("langue_cible", language)
            put("tentative", attempt)
            put("intention", intention)
        }

    /** Compétence déclarée : le noyau la suit ensuite en heures et en paliers. */
    fun competenceCreated(id: String, name: String, category: String): String =
        obj("CompetenceCreated") {
            put("id", id)
            put("nom", name)
            put("categorie", category)
        }

    /** Cible d'heures déclarée : le noyau en déduit les paliers Argent et Or. */
    fun competenceTargetSet(id: String, hours: Long): String =
        obj("CompetenceTargetSet") {
            put("id", id)
            putRaw("heures_cible", hours.coerceAtLeast(1L).toString())
        }

    fun goalCreated(id: String, title: String): String =
        obj("GoalCreated") {
            put("id", id)
            put("titre", title)
        }

    fun goalMissed(goalId: String, title: String): String =
        obj("GoalMissed") {
            put("goal_id", goalId)
            put("titre", title)
        }

    fun growthReflection(goalId: String, reflection: String): String =
        obj("GrowthReflectionCaptured") {
            put("goal_id", goalId)
            put("reflexion", reflection)
        }

    /** Montant en JSON : toujours un nombre décimal, jamais une notation exponentielle. */
    private fun formatAmount(amount: Double): String {
        val cents = kotlin.math.round(amount * 100.0).toLong()
        val whole = cents / 100
        val frac = kotlin.math.abs(cents % 100)
        return "$whole.${frac.toString().padStart(2, '0')}"
    }

    /**
     * Carte de révision jugée sue ou oubliée. C'est le noyau qui en tire la case et
     * la prochaine échéance : la boîte de Leitner est une règle d'apprentissage, elle
     * lui appartient.
     */
    fun flashcardReviewed(cardId: String, skillId: String?, remembered: Boolean): String =
        obj("FlashcardReviewed") {
            put("carte_id", cardId)
            put("competence_id", skillId.orEmpty())
            putRaw("reussie", remembered.toString())
        }

    /** Bornes d'une parcelle, telles que le noyau les impose : ni plus court, ni plus long. */
    private val PARCEL_MINUTES = 5..10

    /**
     * Objectif découpé en parcelles. C'est le seul fait où la *structure* d'un projet
     * entre dans le noyau : jusqu'ici il ne voyait que les résultats.
     *
     * Le noyau refuse une parcelle hors de cinq à dix minutes — c'est la règle
     * anti-procrastination, pas un détail de format. L'application propose déjà des
     * durées dans cet intervalle ; on borne ici ce qui déborderait, et une durée non
     * renseignée prend le haut de la fourchette.
     */
    fun goalDecomposed(goalId: String, parcels: List<Pair<String, Int?>>): String =
        obj("GoalDecomposed") {
            put("goal_id", goalId)
            val items = parcels.joinToString(",") { (name, minutes) ->
                val bounded = (minutes ?: PARCEL_MINUTES.last).coerceIn(PARCEL_MINUTES)
                "{" + escape("nom") + ":" + escape(name) + "," +
                    escape("duree_estimee_minutes") + ":" + bounded + "}"
            }
            putRaw("parcelles", "[$items]")
        }

    // ── Construction du JSON ───────────────────────────────────────
    private class Fields {
        val parts = mutableListOf<String>()
        fun put(name: String, value: String) = parts.add("${escape(name)}:${escape(value)}")
        fun putRaw(name: String, literal: String) = parts.add("${escape(name)}:$literal")
    }

    private fun obj(type: String, build: Fields.() -> Unit): String {
        val fields = Fields().apply(build)
        return "{" + escape("type") + ":" + escape(type) + "," +
            escape("data") + ":{" + fields.parts.joinToString(",") + "}}"
    }

    /** Échappe une chaîne pour un JSON écrit à la main. */
    private fun escape(raw: String): String = buildString {
        append('"')
        raw.forEach { c ->
            when (c) {
                '"' -> append("\\\"")
                '\\' -> append("\\\\")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> append(c)
            }
        }
        append('"')
    }
}
